import { Injectable } from '@angular/core';
import type {
  HistoryAction,
  HistoryItem,
} from '../communication-service/communication.service';
import { SettingsService } from '../setting-service/setting.service';

/**
 * Registro local das ações de comunicação.
 *
 * Enquanto as rotas de `comunicacao/*` não existirem na API, a trilha de
 * auditoria não tem onde ser gravada. Este serviço guarda as ações feitas no
 * painel no `localStorage` do próprio navegador, para que a tela de Histórico
 * possa ser usada com pedidos de verdade vindos da API de dev.
 *
 * Limitações que valem para tudo aqui, e que são intencionais:
 *
 * - o registro é do navegador, não do programa: outra pessoa ou outra máquina
 *   não enxergam nada;
 * - some se os dados de navegação forem limpos;
 * - não serve como auditoria — o próprio usuário consegue alterar;
 * - só registra o que passou pelo painel.
 *
 * Quando a API existir, apague este serviço, troque `USAR_MOCK` para `false` em
 * [communication.service.ts](../communication-service/communication.service.ts)
 * e remova as chamadas em `edit-rescue.ts` e `dispatches.ts`.
 */
const CHAVE_ARMAZENAMENTO = 'COMMUNICATION_HISTORY_LOCAL';

/** Uma ação e os registros que ela afetou, como ficam guardados. */
interface RegistroLocal {
  acao: HistoryAction;
  itens: HistoryItem[];
}

export interface FiltrosHistorico {
  dataInicio?: string;
  dataFim?: string;
  tipo?: string;
  administrador?: string;
  numero_pedido?: string;
  /** Busca pelo nome dado ao disparo pontual. */
  nome?: string;
}

/** Um pedido afetado por uma alteração de status. */
export interface PedidoProcessado {
  numero_pedido: string;
  nome_medico: string;
  status_anterior: string;
}

@Injectable({
  providedIn: 'root',
})
export class LocalHistoryService {
  constructor(private settingsService: SettingsService) {}

  // ------------------------------------------------------------------
  // Escrita
  // ------------------------------------------------------------------

  /** Registra uma alteração de status feita na tela de Pedidos. */
  registrarProcessamento(params: {
    statusNovo: string;
    template: string | null;
    emailEnviado: boolean;
    pedidos: PedidoProcessado[];
  }): HistoryAction {
    const itens: HistoryItem[] = params.pedidos.map((pedido) => ({
      numero_pedido: pedido.numero_pedido,
      nome_medico: pedido.nome_medico,
      status_anterior: pedido.status_anterior,
      status_novo: params.statusNovo,
      email_enviado: params.emailEnviado,
      motivo_falha: params.emailEnviado ? null : 'Sem e-mail nesta ação',
    }));

    return this.gravarAcao(
      {
        tipo: 'processamento',
        acao: `Alterou status para ${params.statusNovo}`,
        quantidade: itens.length,
        template: params.template,
        // Nenhum e-mail sai daqui de verdade: o envio também depende da API.
        sucessos: params.emailEnviado ? itens.length : 0,
        falhas: 0,
      },
      itens,
    );
  }

  /** Registra um disparo pontual feito na tela de Disparos. */
  registrarDisparo(params: {
    nome: string;
    assunto: string;
    template: string | null;
    destinatarios: { nome: string; email: string }[];
  }): HistoryAction {
    const itens: HistoryItem[] = params.destinatarios.map((destinatario) => ({
      numero_pedido: '—',
      nome_medico: destinatario.nome || destinatario.email,
      status_anterior: '—',
      status_novo: '—',
      email_enviado: true,
      motivo_falha: null,
    }));

    return this.gravarAcao(
      {
        tipo: 'disparo',
        // O nome é o que a pessoa vai procurar depois; o assunto entra como
        // contexto, na linha de baixo.
        acao: `Disparo pontual: ${params.nome}`,
        nome: params.nome,
        assunto: params.assunto,
        quantidade: itens.length,
        template: params.template,
        sucessos: itens.length,
        falhas: 0,
      },
      itens,
    );
  }

  private gravarAcao(
    dados: Omit<HistoryAction, 'id_acao' | 'data' | 'administrador'>,
    itens: HistoryItem[],
  ): HistoryAction {
    const registros = this.ler();

    const acao: HistoryAction = {
      id_acao: this.proximoId(registros),
      data: new Date().toISOString(),
      administrador: this.administradorAtual(),
      ...dados,
    };

    registros.push({ acao, itens });
    this.gravar(registros);

    return acao;
  }

  /**
   * Quem fez a ação sai do usuário logado. Na API isso viria do `auth-token`,
   * justamente para não poder ser forjado — aqui é só informativo.
   */
  private administradorAtual(): { nome: string; email: string } {
    const usuario = this.settingsService.user || {};

    return {
      nome: usuario.nome || 'Administrador',
      email: usuario.email || '',
    };
  }

  private proximoId(registros: RegistroLocal[]): number {
    return registros.reduce((maior, registro) => Math.max(maior, registro.acao.id_acao), 0) + 1;
  }

  // ------------------------------------------------------------------
  // Leitura
  // ------------------------------------------------------------------

  /** Ações que passam pelos filtros, da mais recente para a mais antiga. */
  getAcoes(filtros: FiltrosHistorico = {}): HistoryAction[] {
    return this.ler()
      .filter((registro) => this.combina(registro, filtros))
      .map((registro) => registro.acao)
      .sort((a, b) => b.id_acao - a.id_acao);
  }

  getItens(idAcao: number): HistoryItem[] {
    const registro = this.ler().find((item) => item.acao.id_acao === idAcao);
    return registro ? registro.itens : [];
  }

  temRegistros(): boolean {
    return this.ler().length > 0;
  }

  limpar() {
    localStorage.removeItem(CHAVE_ARMAZENAMENTO);
  }

  private combina(registro: RegistroLocal, filtros: FiltrosHistorico): boolean {
    const acao = registro.acao;

    if (filtros.tipo && acao.tipo !== filtros.tipo) {
      return false;
    }

    // As datas chegam como YYYY-MM-DD; comparar só a parte da data evita que um
    // registro feito hoje à tarde fique de fora do filtro que termina hoje.
    const dia = (acao.data || '').slice(0, 10);

    if (filtros.dataInicio && dia < filtros.dataInicio) {
      return false;
    }

    if (filtros.dataFim && dia > filtros.dataFim) {
      return false;
    }

    if (filtros.administrador) {
      const busca = filtros.administrador.toLowerCase();
      const alvo = `${acao.administrador.nome} ${acao.administrador.email}`.toLowerCase();

      if (!alvo.includes(busca)) {
        return false;
      }
    }

    if (filtros.nome) {
      const busca = filtros.nome.toLowerCase().trim();

      if (!(acao.nome || '').toLowerCase().includes(busca)) {
        return false;
      }
    }

    // Busca reversa: a ação entra se afetou o pedido procurado.
    if (filtros.numero_pedido) {
      const busca = filtros.numero_pedido.toLowerCase().trim();
      const afetou = registro.itens.some((item) =>
        (item.numero_pedido || '').toLowerCase().includes(busca),
      );

      if (!afetou) {
        return false;
      }
    }

    return true;
  }

  // ------------------------------------------------------------------
  // Armazenamento
  // ------------------------------------------------------------------

  private ler(): RegistroLocal[] {
    try {
      const bruto = localStorage.getItem(CHAVE_ARMAZENAMENTO);
      const conteudo = bruto ? JSON.parse(bruto) : [];

      return Array.isArray(conteudo) ? conteudo : [];
    } catch {
      // Conteúdo corrompido não pode derrubar a tela: começa do zero.
      return [];
    }
  }

  private gravar(registros: RegistroLocal[]) {
    try {
      localStorage.setItem(CHAVE_ARMAZENAMENTO, JSON.stringify(registros));
    } catch {
      // Sem espaço ou armazenamento bloqueado: a ação principal já aconteceu,
      // então o registro é perdido em silêncio em vez de virar erro na tela.
    }
  }
}
